import { Flex } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';

import { Tooltip } from '@suntex.fim/ui';

import { MAX_MESSAGE_LENGTH } from '../../constants';
import { htmlToMarkdown, richTextFormats } from '../../utils';

/** Quill toolbar modules (no image handler — plain toolbar). */
const modules = {
  toolbar: [
    ['bold', 'italic'],
    [{ header: '1' }, { header: '2' }, { font: [] }],
    [{ size: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
  ],
  clipboard: { matchVisual: false },
};

export type RichTextMeta = {
  characterCount: number;
};

type RichTextProps = {
  initialValue?: string;
  onChange?: (value: string, meta: RichTextMeta) => void;
};

/** Controlled rich-text editor with character-count display. */
export function RichText({ initialValue = '', onChange }: RichTextProps) {
  const [value, setValue] = useState<string>(initialValue);
  const quillRef = useRef<ReactQuill>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const characterCount = useMemo(() => htmlToMarkdown(value).length, [value]);

  const handleChange = useCallback(
    (content: string, _delta: unknown, source: string) => {
      if (source === 'user') {
        setValue(content);
        const count = htmlToMarkdown(content).length;
        onChange?.(content, { characterCount: count });
      }
    },
    [onChange],
  );

  const disableCursor = (event: {
    key: string;
    preventDefault: () => void;
  }) => {
    if (characterCount >= MAX_MESSAGE_LENGTH && event.key !== 'Backspace') {
      event.preventDefault();
    }
  };

  return (
    <div className="richtext-editor" style={{ marginBottom: '15px' }}>
      <ReactQuill
        ref={quillRef}
        onChange={handleChange}
        onKeyDown={disableCursor}
        value={value}
        modules={modules}
        formats={[...richTextFormats]}
        style={{ direction: 'ltr' }}
      />
      <Flex
        className="richtext-info text-chalkboard-txt"
        justify="space-between"
      >
        <Tooltip title="Formatting may add to your character count.">
          <span className="richtext-info__desc">
            Used <span className="fw-bold">{characterCount}</span> of{' '}
            <span className="fw-bold">{MAX_MESSAGE_LENGTH}</span> characters.
          </span>
        </Tooltip>
      </Flex>
    </div>
  );
}
